import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { jsPDF } from "jspdf";                      // ← STATIC import, no dynamic import()
import { PURCHASE_API, MASTER_API } from "../../../apiConfig";
import sbeeLogo from "../../../assets/sbeelogo.png";
import { authHeaders } from "../../../utils/authHeaders"; // ⚠️ ADJUST if this file's actual depth differs

// Created By / Approved By are stored as "First Last". Show them as-is; only
// legacy values (an email or user id) are tidied up so they stay readable.
const getDisplayName = (value) => {
  if (!value) return "";
  const v = String(value).trim();
  if (v.includes(" ")) return v; // already a full name
  const namePart = v.split("@")[0];
  return namePart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(" ");
};

const formatDate = (val) => (val ? new Date(val).toLocaleDateString() : "");

// sales_order is stored in the DB as an array (usually saved as a JSON
// string, e.g. '["SO-1001","SO-1002"]'), but can also already arrive as a
// real array or a plain comma-separated string depending on the caller.
// This normalizes all of those into one "SO-1001, SO-1002" display string.
const formatSalesOrder = (val) => {
  if (val === null || val === undefined || val === "") return "";
  let arr = val;
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (trimmed.startsWith("[")) {
      try {
        arr = JSON.parse(trimmed);
      } catch {
        return trimmed; // not valid JSON, fall back to showing as-is
      }
    } else {
      return trimmed; // already a plain/comma-separated string
    }
  }
  if (Array.isArray(arr)) return arr.filter(Boolean).join(", ");
  return String(arr);
};

// Only show an approver name once the PR is actually approved.
// `approved_by` comes straight from the purchase_request table.
const getApprovedByName = (row) => {
  if (!row) return "";
  const status = String(row.approval_status ?? row.pr_status ?? "").toLowerCase();
  if (status !== "approved") return "";
  return getDisplayName(row.approved_by);
};

const PurchaseRequisitionView = () => {
  const { prNo } = useParams();
  const [searchParams] = useSearchParams();
  const [prData, setPrData] = useState([]);
  const [statusMsg, setStatusMsg] = useState("Loading...");

  // Refs so PDF function always reads latest data (no stale closure)
  const prRef     = useRef([]);
  const didTrigger = useRef(false); // prevent double-trigger

  // Unit 1 (Regd. Office) address, from Configuration > Location Master —
  // shown under the company name on both the on-screen slip and the PDF.
  const [unit1Location, setUnit1Location] = useState(null);
  const unit1Ref = useRef(null); // ref mirror so the PDF builder never reads a stale/empty value

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPR(); }, [prNo]);

  const loadUnit1Location = async () => {
    try {
      const res = await fetch(`${MASTER_API}/location-master`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      const locations = Array.isArray(data) ? data : [];
      const unit1 = locations.find((loc) => (loc.location_label || "").toLowerCase().includes("1")) || null;
      unit1Ref.current = unit1;
      setUnit1Location(unit1);
    } catch (err) {
      console.error("LOCATION MASTER FETCH ERROR", err);
      unit1Ref.current = null;
      setUnit1Location(null);
    }
  };

  const loadPR = async () => {
    try {
      const [res] = await Promise.all([
        fetch(`${PURCHASE_API}/purchase-request/lines?purchase_request_no=${encodeURIComponent(prNo)}`, {
          headers: authHeaders(),
        }),
        loadUnit1Location(),
      ]);
      const json = await res.json();
      const rows = json?.success ? (json.data || []) : [];
      prRef.current = rows;
      setPrData(rows);
      setStatusMsg(rows.length ? "ready" : (json?.message || "No records found for this PR."));

      // Trigger action AFTER data is in refs
      if (!didTrigger.current) {
        didTrigger.current = true;
        const action = searchParams.get("action");
        if (action === "download") buildAndSavePDF(rows);
        else if (action === "print") setTimeout(() => window.print(), 400);
      }
    } catch (err) {
      console.error("PR load error:", err);
      setStatusMsg("Error: " + err.message);
    }
  };

  // ── Convert image src → base64 safely ──────────────────────────────────────
  const toBase64 = (src) =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const c = document.createElement("canvas");
          c.width  = img.naturalWidth  || 400;
          c.height = img.naturalHeight || 120;
          c.getContext("2d").drawImage(img, 0, 0);
          resolve(c.toDataURL("image/png"));
        } catch { resolve(null); }
      };
      img.onerror = () => resolve(null);
      // cache-bust to avoid opaque response
      img.src = src.startsWith("data:") ? src : src + "?cb=" + Date.now();
    });

  // ── Core PDF builder — Material Requisition Slip layout ─────────────────────
  const buildAndSavePDF = async (rows) => {
    if (!rows || rows.length === 0) return;

    const pdf  = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const PW   = 210, PH = 297, ML = 14, MR = 14, CW = PW - ML - MR;
    const HH = 14, RH = 9; // table header / row heights (also used below)

    const first = rows[0] || {};

    // ---- Logo (fetched up front so we can measure before drawing) ----
    const logoB64 = await toBase64(sbeeLogo);

    // ---- Work out the slip's total height so it can be vertically centered ----
    const logoBlockH = logoB64 ? 16 : 4;
    const contentHeight = logoBlockH + 11 /*title*/ + 4 /*address*/ + 11 /*subtitle*/ + 11 /*slip row*/
      + 11 /*sales order row*/ + 5 /*instruction*/ + HH + rows.length * RH /*table*/ + 14 + 10 + 6 /*signatures*/;

    const topMargin = 14;
    let y = contentHeight + topMargin * 2 <= PH
      ? Math.max(topMargin, (PH - contentHeight) / 2)
      : topMargin;

    const guard = (need = 10) => { if (y + need > 282) { pdf.addPage(); y = 14; } };
    const hline = (x1,y1,x2,y2) => { pdf.setDrawColor(0); pdf.line(x1,y1,x2,y2); };

    const boxTop = y;

    // ---- Logo ----
    if (logoB64) {
      const lw = 40;
      pdf.addImage(logoB64, "PNG", (PW-lw)/2, y + 2, lw, 12);
      y += 16;
    } else {
      y += 4;
    }

    // ---- Title ----
    const unit1 = unit1Ref.current;
    const unit1AddressLine = unit1
      ? `${unit1.address_line1}${unit1.address_line2 ? ", " + unit1.address_line2 : ""}, ${unit1.city}${unit1.pin_code ? " - " + unit1.pin_code : ""}, ${unit1.state}`
      : "No.282 & 283, KIADB Industrial Area, Phase 2, Harohalli, Kanakapura Taluk, Ramanagar - 562112, Karnataka, INDIA";

    pdf.setFontSize(15); pdf.setFont("helvetica","bold");
    pdf.text("SBEE CABLES (INDIA) LIMITED", PW/2, y, {align:"center"});
    y += 5;
    pdf.setFontSize(8.5); pdf.setFont("helvetica","normal");
    pdf.text(unit1AddressLine, PW/2, y, {align:"center"});
    y += 3;
    hline(ML, y, ML+CW, y);
    y += 7;

    // ---- Sub-title ----
    pdf.setFontSize(12); pdf.setFont("helvetica","bold");
    pdf.text("MATERIAL REQUISITION SLIP", PW/2, y, {align:"center"});
    y += 4;
    hline(ML, y, ML+CW, y);
    y += 7;

    // ---- Slip No / Department / Date row ----
    pdf.setFontSize(9.5);
    pdf.setFont("helvetica","bold");   pdf.text("Slip No:", ML, y);
    pdf.setFont("helvetica","normal"); pdf.text(String(first.purchase_request_no ?? prNo ?? ""), ML+16, y);

    pdf.setFont("helvetica","bold");   pdf.text("", ML+70, y);
    pdf.setFont("helvetica","normal"); pdf.text(String(first.department ?? ""), ML+95, y);

    pdf.setFont("helvetica","bold");   pdf.text("Date:", ML+150, y);
    pdf.setFont("helvetica","normal"); pdf.text(formatDate(first.created_at), ML+162, y);
    y += 4;
    hline(ML, y, ML+CW, y);
    y += 7;

    // ---- Sales Order row ----
    pdf.setFont("helvetica","bold");   pdf.text("Sales Order:", ML, y);
    pdf.setFont("helvetica","normal");
    const salesOrderText = pdf.splitTextToSize(formatSalesOrder(first.sales_order) || "-", CW - 24)[0] ?? "-";
    pdf.text(salesOrderText, ML+24, y);
    y += 4;
    hline(ML, y, ML+CW, y);
    y += 7;

    // ---- Instruction line ----
    pdf.setFontSize(9.5); pdf.setFont("helvetica","bold");
    pdf.text("Kindly issue the following items", ML, y);
    y += 5;

    // ---- Table ----
    const cols = [
      { key: "sl",      label: "Sl No",           w: 14 },
      { key: "code",    label: "Material Code",   w: 32 },
      { key: "desc",    label: "Item Description",w: CW - 14 - 32 - 22 - 18 - 34 },
      { key: "qty",     label: "Qty",              w: 22 },
      { key: "uom",     label: "UOM",              w: 18 },
      { key: "remarks", label: "Special Remarks", w: 34 },
    ];
    guard(HH);
    pdf.setFontSize(8.5); pdf.setFont("helvetica","bold");
    const tableTop = y;
    let cx = ML;
    cols.forEach(col=>{
      if (cx > ML) hline(cx, y, cx, y+HH);
      const lines = pdf.splitTextToSize(col.label, col.w - 2);
      const startY = y + HH/2 - ((lines.length-1)*3.5)/2 + 2;
      lines.forEach((ln, i) => pdf.text(ln, cx+col.w/2, startY + i*4, {align:"center"}));
      cx += col.w;
    });
    hline(ML, y, ML+CW, y);       // top border of header
    y += HH;
    hline(ML, y, ML+CW, y);       // bottom border of header

    pdf.setFont("helvetica","normal"); pdf.setFontSize(9);
    rows.forEach((item, idx) => {
      guard(RH);
      const cells = [
        idx+1,
        item.product_code ?? "",
        item.description ?? "",
        item.requirement_qty ?? "",
        item.uom ?? "",
        item.special_remarks ?? item.remarks ?? "",
      ];
      cx = ML;
      cols.forEach((col, ci) => {
        if (cx > ML) hline(cx, y, cx, y+RH);
        const align = ci === 2 ? "left" : "center";
        const tx = align === "left" ? cx+2 : cx+col.w/2;
        const v = pdf.splitTextToSize(String(cells[ci]), col.w-3)[0] ?? "";
        pdf.text(v, tx, y+5.8, {align});
        cx += col.w;
      });
      y += RH;
      hline(ML, y, ML+CW, y);
    });

    // side borders for the table
    pdf.setDrawColor(0);
    pdf.line(ML, tableTop, ML, y);
    pdf.line(ML+CW, tableTop, ML+CW, y);

    y += 14;
    guard(30);

    // ---- Requested By / Approved By ----
    const halfW = CW/2;
    pdf.setFontSize(10); pdf.setFont("helvetica","bold");
    pdf.text("Requested By:", ML, y);
    pdf.text("Approved By:", ML+halfW, y);
    y += 10;

    pdf.setFont("helvetica","bold"); pdf.text("Name:", ML, y);
    pdf.setFont("helvetica","normal"); pdf.text(getDisplayName(first.created_by), ML+16, y);

    pdf.setFont("helvetica","bold"); pdf.text("Name:", ML+halfW, y);
    pdf.setFont("helvetica","normal"); pdf.text(getApprovedByName(first), ML+halfW+16, y);
    y += 6;

    // ---- Outer border around the whole slip ----
    pdf.setDrawColor(0);
    pdf.rect(ML, boxTop, CW, y - boxTop);

    pdf.save(`${prNo}_MaterialRequisitionSlip.pdf`);
  };

  // Styles for the on-screen view
  const th  = {border:"1px solid black",padding:"8px",textAlign:"center",fontWeight:"bold",fontSize:"14px"};
  const td  = {border:"1px solid black",padding:"8px",textAlign:"center",fontSize:"14px"};
  const tdL = {border:"1px solid black",padding:"8px",textAlign:"left",fontSize:"14px"};

  const first = prData[0] || {};

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #pr-container, #pr-container * { visibility: visible; }
          #pr-container { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
          @page { size: A4; margin: 10mm; }
        }
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px 0",
          boxSizing: "border-box",
        }}
      >
        {prData.length > 0 && (
          <div className="no-print" style={{width:"1000px",margin:"0 0 10px",display:"flex",justifyContent:"flex-end",gap:"8px"}}>
            <button onClick={() => window.print()} style={{padding:"6px 14px",fontSize:"13px",cursor:"pointer"}}>Print</button>
            <button onClick={() => buildAndSavePDF(prData)} style={{padding:"6px 14px",fontSize:"13px",cursor:"pointer"}}>Download PDF</button>
          </div>
        )}

        <div
          id="pr-container"
          style={{width:"1000px",border:"1px solid black",
                  fontFamily:"Calibri,Arial,sans-serif",background:"#fff"}}
        >
        {prData.length === 0 ? (
          <div style={{padding:20,color:"#64748b"}}>{statusMsg}</div>
        ) : (
          <>
            <div style={{textAlign:"center",padding:"10px 15px 6px",borderBottom:"1px solid black"}}>
              <img src={sbeeLogo} alt="SBEE" style={{width:"160px",height:"auto",marginBottom:"6px"}} />
              <h2 style={{margin:0,fontSize:"20px",fontWeight:"bold",fontFamily:"'Good Times', 'Segoe UI', Calibri, Arial, sans-serif"}}>SBEE CABLES (INDIA) LIMITED</h2>
              {/* Unit 1 (Regd. Office) address, from Configuration > Location Master */}
              <div style={{fontSize:"12px",marginTop:"4px",color:"#333"}}>
                {unit1Location
                  ? `${unit1Location.address_line1}${unit1Location.address_line2 ? ", " + unit1Location.address_line2 : ""}, ${unit1Location.city}${unit1Location.pin_code ? " - " + unit1Location.pin_code : ""}, ${unit1Location.state}`
                  : "No.282 & 283, KIADB Industrial Area, Phase 2, Harohalli, Kanakapura Taluk, Ramanagar - 562112, Karnataka, INDIA"}
              </div>
            </div>

            <div style={{textAlign:"center",padding:"6px 15px",borderBottom:"1px solid black"}}>
              <h3 style={{margin:0,fontSize:"16px",fontWeight:"bold"}}>MATERIAL REQUISITION SLIP</h3>
            </div>

            <div style={{display:"flex",justifyContent:"space-between",padding:"8px 15px",borderBottom:"1px solid black",fontSize:"14px"}}>
              <span><b>Slip No:</b> {first.purchase_request_no ?? prNo}</span>
              <span><b></b> {first.department ?? ""}</span>
              <span><b>Date:</b> {formatDate(first.created_at)}</span>
            </div>

            <div style={{padding:"8px 15px",borderBottom:"1px solid black",fontSize:"14px"}}>
              <b>Sales Order:</b> {formatSalesOrder(first.sales_order) || "-"}
            </div>

            <div style={{padding:"8px 15px",fontWeight:"bold",fontSize:"14px"}}>
              Kindly issue the following items
            </div>

            <table style={{width:"100%",borderCollapse:"collapse",marginBottom:0}}>
              <thead>
                <tr>
                  <th style={th}>Sl No</th>
                  <th style={th}>Material Code</th>
                  <th style={th}>Item Description</th>
                  <th style={th}>Qty</th>
                  <th style={th}>UOM</th>
                  <th style={th}>Special Remarks</th>
                </tr>
              </thead>
              <tbody>
                {prData.map((item, index) => (
                  <tr key={item.id ?? index}>
                    <td style={td}>{index+1}</td>
                    <td style={td}>{item.product_code || "-"}</td>
                    <td style={tdL}>{item.description}</td>
                    <td style={td}>{item.requirement_qty}</td>
                    <td style={td}>{item.uom || "-"}</td>
                    <td style={td}>{item.special_remarks ?? item.remarks ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            

            <div style={{display:"flex",justifyContent:"space-between",padding:"15px",borderTop:"1px solid black"}}>
              <div style={{fontSize:"14px"}}>
                <p style={{margin:"0 0 10px"}}><b>Requested By:</b></p>
                <p style={{margin:0}}><b>Name:</b> {getDisplayName(first.created_by)}</p>
              </div>
              <div style={{fontSize:"14px"}}>
                <p style={{margin:"0 0 10px"}}><b>Approved By:</b></p>
                <p style={{margin:0}}><b>Name:</b> {getApprovedByName(first)}</p>
              </div>
            </div>
          </>
        )}
        </div>
      </div>
    </>
  );
};

export default PurchaseRequisitionView;